import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/session/api/index.api';
import type {
  SessionGet,
  SessionCreate,
  SessionClone,
  SessionUpdate,
  SessionDelete,
  SessionSigningMethodsGet,
  SessionSigningPrepare,
} from '@modules/store/session/types/SessionAPI.interface';
import { SessionPreview } from '@modules/store/session/types/SessionAPI.interface';
import type SessionState from '@modules/store/session/types/SessionState.entity';
import { ActionTree } from 'vuex';

export const actions: ActionTree<SessionState, CommonState> = {
  fetchSession: ({ commit }, get: SessionGet): Promise<void> => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .getSession(get)
      .then((data: any) => {
        commit('mergeSession', data.session);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|getSession', error.message);
        throw new Error('fetchSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  createSession: ({ commit }, create: SessionCreate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .createSession(create)
      .then((data: any) => {
        commit('mergeSession', data.session);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|createSession', error.message);
        throw new Error('createSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  cloneSession: ({ commit }, clone: SessionClone) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .cloneSession(clone)
      .then((data: any) => {
        commit('mergeSession', data.session);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|cloneSession', error.message);
        throw new Error('cloneSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  updateSession: async ({ commit }, update: SessionUpdate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .updateSession(update)
      .then((data: any) => {
        commit('mergeSession', data.session);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|updateSession', error.message);
        throw new Error('updateSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  deleteSession: async ({ commit }, del: SessionDelete) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .deleteSession(del)
      .then(() => {
        commit('setSession', {});
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|deleteSession', error.message);
        throw new Error('deleteSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  fetchSessionPreview: async ({ commit }, preview: SessionPreview) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .fetchSessionPreview(preview)
      .then((data: any) => {
        commit('setSessionPreview', data);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|fetchSessionPreview', error.message);
        throw new Error('fetchSessionPreview failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  fetchSessionSigningMethods: ({ commit }, get: SessionSigningMethodsGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .getSessionSigningMethods(get)
      .then((data: any) => {
        commit('setSessionSigningMethods', data.signingMethods);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|fetchSessionSigningMethods', error.message);
        throw new Error('fetchSessionSigningMethods failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
  prepareSessionSigning: ({ commit }, prepare: SessionSigningPrepare) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setSessionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setSessionApiError', false);
    return new api()
      .prepareSessionSigning(prepare)
      .then((data: any) => {
        commit('setSessionSigningUuid', data.uuid);
        commit('setLandlordSignerUuid', data.landlordSignerUuid);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setSessionApiError', true);
        LogOperations.error('Session|Actions|prepareSessionSigning', error.message);
        throw new Error('prepareSessionSigning failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setSessionLoading', false);
      });
  },
};
